import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import { DateRangePicker, Stack } from "rsuite";
import { BiSearch, BiReset, BiDownload } from "react-icons/bi";
import { BsCloudDownloadFill, BsEye } from "react-icons/bs";
import Sidebar from "../../includes/Sidebar";
import { downloadXl, predefinedBottomRanges, convertDateToStr } from "../../../functions";
import { Tooltip } from "@mui/material";
import { seErrorMessage } from "../../../slice/error/errorSlice";
import { useDispatch } from "react-redux";
import { SftpHouseKeepingService } from "../../../services/SftpHouseKeepingService";
import Select from "react-select";
import useCacheFilter from "../../../utils/useCacheFilter";


const datatableCustomStyles = {
  rows: {
    style: {
      minHeight: '20px',
      fontSize: '0.7em',
      fontColor: '#212529 !important',
      width: '100%'
    },
  },
  headCells: {
    style: {
      paddingLeft: '5px',
      paddingRight: '5px',
      textTransform: "uppercase",
      fontSize: '0.7em',
      fontWeight: '800',
      fontColor: '#6c757d !important',
      opacity: 1,
      minHeight: "10px",
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      width: "100%",
    },
  },
  pagination: {
    style: {
      color: '#000',
      fontSize: '13px',
      minHeight: '56px',
      backgroundColor: '#fff',
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
      borderTopColor: '#f2f2f2',
    },
    pageButtonsStyle: {
      borderRadius: '50%',
      height: '40px',
      width: '40px',
      padding: '8px',
      margin: '0px',
      cursor: 'pointer',
      transition: '0.4s',
      color: '#000',
      fill: '#000',
      backgroundColor: 'transparent',
      '&:disabled': {
        cursor: 'unset',
        color: '#e2e2e2',
        fill: '#e2e2e2',
      },
      '&:hover:not(:disabled)': {
        backgroundColor: '#f2f2f2',
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: '#f5f5f5',
      },
    },
  },
};

const types = [
  { value: "Report", label: "Report" },
  { value: "Deleted", label: "Deleted" },
];

const selectStyles = {
  container: (styles) => ({
    ...styles,
    borderRadius: "9px",
    width: "100%"
  }),
  control: (styles, state) => ({
    ...styles,
    backgroundColor: "hsl(204deg 33.33% 97.06%)",
    borderStyle: "none",
    boxShadow: "none",
    borderRadius: "10px",
    maxHeight: "30px",
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    width: "0px",
    minHeight: "20px",
    maxHeight: "30px",
  }),
  valueContainer: (styles) => ({
    ...styles,
    maxHeight: "30px",
    overflow: "scroll",
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? "#dbf4f5" : "inherit",
    color: state.isSelected ? "#000000" : "inherit",
    "&:hover": { backgroundColor: state.isSelected ? "#dbf4f5" : "rgb(222, 235, 255)" },
  }),
};

function SftpHouseKeepingLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageLocation = useLocation();
  const pageKey = pageLocation.pathname.split('/')[1] || 'sftp-house-keeping-logs';
  const [filter, updateFilter, clearCacheFilters] = useCacheFilter(pageKey, '');

  const [pageno, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [colorRow, setColorRow] = useState({});
  const [data, setData] = useState({});
  const [limit, setLimit] = useState(100);
  const [searchedData, setSearchedData] = useState({
    type: [],
    date: "",
  });

  const [isInitSearch, setIsInitSearch] = useState(true);
  const [isFocusedType, setIsFocusedType] = useState(false);

  const sftpHouseKeepingService = new SftpHouseKeepingService();
  const { getRetrieveSftpHouseKeepingLogs, getHouseKeepingLog } = sftpHouseKeepingService;

  const [downloadingLogId, setDownloadingLogId] = useState(null);

  const handleDownloadLog = useCallback((row) => {
    if (!row?.log) return;
    const rowId = row?.id || row?._id || row?.log;
    setDownloadingLogId(rowId);

    getHouseKeepingLog(row.log)
      .then((response) => {
        setDownloadingLogId(null);
        const url = response?.file_url
        if (url && typeof url === "string") {
          downloadXl(url);
        } else {
          dispatch(seErrorMessage({ message: "Download failed: No URL returned", is_show: true }));
        }
      })
      .catch(() => {
        setDownloadingLogId(null);
        dispatch(seErrorMessage({ message: "An error occurred while fetching log file.", is_show: true }));
      });
  }, [dispatch, getHouseKeepingLog]);

  const handleRowClick = (row) => {
    setColorRow(row);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLimit(newPerPage);
    setPageNo(1);
    fetchData(1, newPerPage, searchedData);
    updateFilter({
      defaultValue: {
        ...searchFormik.values,
        pageno: 1,
        limit: newPerPage,
        isInitSearch
      }
    });
  };

  const handlePageChange = (page) => {
    setPageNo(page);
    fetchData(page, limit, searchedData);
    updateFilter({
      defaultValue: {
        ...searchFormik.values,
        pageno: page,
        limit,
        isInitSearch
      }
    });
  };

  const fetchData = (page, limit, values, export_type = false, header_names = []) => {
    if (export_type) {
      setExportLoading(true);
    } else {
      setLoading(true);
    }

    const date = Array.isArray(values.date) && values.date.length > 0
      ? convertDateToStr(values.date, false)
      : (typeof values.date === "string" ? values.date : "");

    getRetrieveSftpHouseKeepingLogs({
      page,
      limit,
      type: values.type,
      date,
      export_type,
      header_names
    }).then((response) => {
      if (export_type) {
        setExportLoading(false);
        const url = response?.data?.export_file || response?.data?.url || response?.data;
        if (url) {
          downloadXl(url);
        } else {
          dispatch(seErrorMessage({ message: "Export failed: No download URL returned", is_show: true }));
        }
      } else {
        setData(response?.data || {});
        setLimit(limit);
        setPageNo(page);
        setTotalRows(response?.data?.count ?? (Array.isArray(response?.data) ? response?.data.length : (response?.data?.results?.length || 0)));
        setLoading(false);
      }
    }).catch((err) => {
      setLoading(false);
      setExportLoading(false);
      dispatch(seErrorMessage({ message: "An error occurred while fetching reports data.", is_show: true }));
    });
  };

  const searchFormik = useFormik({
    initialValues: {
      type: [],
      date: "",
    },
    onSubmit: (values) => {
      setIsInitSearch(false);
      setSearchedData(values);
      fetchData(1, limit, values);
      updateFilter({
        defaultValue: {
          ...values,
          pageno: 1,
          limit,
          isInitSearch: false
        }
      });
    }
  });

  const handleReset = () => {
    clearCacheFilters();
    searchFormik.resetForm({
      values: {
        type: [],
        date: "",
      }
    });
    setIsInitSearch(true);
    setData({});
    setTotalRows(0);
    setPageNo(1);
    setSearchedData({
      type: [],
      date: "",
    });
  };

  const handleOnDateTimeChange = ({ name, value }) => {
    searchFormik.setFieldValue(name, value ?? "");
  };

  // Restore cached filter state on navigation
  useEffect(() => {
    if (
      filter?.filterValue?.defaultValue &&
      Object.keys(filter?.filterValue?.defaultValue).length > 0
    ) {
      const cached = filter.filterValue.defaultValue;
      const { pageno: cachedPage, limit: cachedLimit, isInitSearch: cachedInit, ...formValues } = cached;

      const restoredPage = Number(cachedPage) || 1;
      const restoredLimit = Number(cachedLimit) || 100;
      setPageNo(restoredPage);
      setLimit(restoredLimit);

      let parsedValues = {
        type: formValues.type || [],
        date: formValues.date || "",
      };

      if (Array.isArray(parsedValues.date)) {
        parsedValues.date = [
          parsedValues.date[0] ? new Date(parsedValues.date[0]) : null,
          parsedValues.date[1] ? new Date(parsedValues.date[1]) : null,
        ];
      }

      searchFormik.setValues(parsedValues);
      setSearchedData(parsedValues);

      if (cachedInit === false) {
        setIsInitSearch(false);
        fetchData(restoredPage, restoredLimit, parsedValues);
      }
      clearCacheFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'S.NO',
        name: 'S.NO',
        selector: (row, index) =>
          Number(pageno) > 1
            ? (Number(pageno) - 1) * Number(limit) + index + 1
            : index + 1,
        wrap: true,
        width: "60px",
        grow: 0.1,
      },
      {
        id: 'date',
        name: 'Date',
        exportKey: 'DATE',
        selector: row => row?.date,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.date}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'type',
        name: 'Type',
        exportKey: 'TYPE',
        selector: row => row?.type,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.type}</span>,
        left: true,
        wrap: true,
        width: "110px",
      },
      {
        id: 'total_files',
        name: 'Total Files',
        exportKey: 'TOTAL FILES',
        selector: row => row?.total_files,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.total_files}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'total_size_bytes',
        name: 'Total Size',
        exportKey: 'TOTAL SIZE',
        selector: row => row?.total_size,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.total_size}</span>,
        left: true,
        wrap: true,
        width: "150px",
      },
      {
        id: 'started_at',
        name: 'Started At',
        exportKey: 'STARTED AT',
        selector: row => row?.started_at,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.started_at}</span>,
        left: true,
        wrap: true,
        width: "170px",
      },
      {
        id: 'completed_at',
        name: 'Completed At',
        exportKey: 'COMPLETED AT',
        selector: row => row?.completed_at,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.completed_at}</span>,
        left: true,
        wrap: true,
        width: "170px",
      },
      {
        id: 'action',
        name: 'Action',
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">

            {row?.log ? (
              <Tooltip disableInteractive title="Download Log" placement="top" arrow>
                <Button
                  variant="light"
                  size="sm"
                  disabled={downloadingLogId === (row?.id || row?._id || row?.log)}
                  className="btn-icon btn-sm py-1 px-2"
                  style={{ color: "#1ba1a6", display: "inline-flex", alignItems: "center" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadLog(row);
                  }}
                >
                  {downloadingLogId === (row?.id || row?._id || row?.log) ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                  ) : (
                    <BiDownload size={15} />
                  )}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="light"
                size="sm"
                className="btn-icon btn-sm py-1 px-2"
                style={{ visibility: "hidden", pointerEvents: "none" }}
                tabIndex={-1}
                aria-hidden="true"
              >
                <BiDownload size={15} />
              </Button>
            )}

            <Tooltip disableInteractive title="Detailed View" placement="top" arrow>
              <Button
                variant="light"
                size="sm"
                className="btn-icon btn-sm py-1 px-2"
                style={{ color: "#1ba1a6", display: "inline-flex", alignItems: "center" }}
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilter({
                    defaultValue: {
                      ...searchFormik.values,
                      pageno,
                      limit,
                      isInitSearch
                    }
                  });
                  navigate(`/sftp-house-keeping/${row?.id || row?._id || ''}`);
                }}
              >
                <BsEye size={15} />
              </Button>
            </Tooltip>

          
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "110px",
      }
    ],
    [limit, pageno, navigate, updateFilter, searchFormik.values, isInitSearch, downloadingLogId, handleDownloadLog]
  );

  const handleExport = () => {
    const headers = columns.filter(i => i.exportKey !== undefined).map(i => i.exportKey);
    fetchData(pageno, limit, searchFormik.values, true, headers);
  };

  const conditionalRowStyles = [
    {
      when: (row) => (row?.id ? colorRow?.id === row?.id : colorRow === row),
      style: () => ({
        backgroundColor: '#dbf4f5',
        color: 'black',
      }),
    },
  ];

  return (
    <React.Fragment>
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle tab-fixed-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 id='page-title'>SFTP House Keeping Logs</h1>
          {data?.deleted_file_size && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              backgroundColor: '#fff3cd', color: '#856404',
              border: '1px solid #ffc107', borderRadius: '6px',
              padding: '3px 10px', fontSize: '0.78em', fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              <span style={{ fontSize: '0.9em' }}>🗑️</span>
              Deleted File Size: <strong>{data?.deleted_file_size}</strong>
            </span>
          )}
          {data?.pending_file_size && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              backgroundColor: '#d1ecf1', color: '#0c5460',
              border: '1px solid #17a2b8', borderRadius: '6px',
              padding: '3px 10px', fontSize: '0.78em', fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              <span style={{ fontSize: '0.9em' }}>⏳</span>
              Pending File Size: <strong>{data?.pending_file_size}</strong>
            </span>
          )}
        </div>

        <section className="section mt-5 ">
          <div className="row align-items-top py-2 gap-2 gap-md-5">
            <div className="col-lg-12">
              <div className="card mb-5 mb-xl-8 mt-3">
                <Form onSubmit={searchFormik.handleSubmit} onReset={handleReset}>
                  <div className=" ">
                    <div className="px-1 py-1 d-flex pe-2 flex-row gap-2 align-items-center flex-wrap">

                      <div style={{ width: '200px', position: 'relative' }}>
                        <Select
                          isMulti
                          options={types}
                          className="select-search"
                          classNamePrefix="select"
                          placeholder="Select Type"
                          isClearable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={true}
                          name="type"
                          onChange={(selected) => {
                            const values = selected ? selected.map(option => option.value) : [];
                            searchFormik.setFieldValue("type", values);
                          }}
                          value={types.filter(option =>
                            searchFormik.values.type?.includes(option.value)
                          )}
                          onFocus={() => setIsFocusedType(true)}
                          onBlur={() => setIsFocusedType(false)}
                          styles={{
                            ...selectStyles,
                            control: (styles, state) => ({
                              ...selectStyles.control(styles, state),
                              maxHeight: isFocusedType ? "auto" : "30px",
                            }),
                            valueContainer: (styles, state) => ({
                              ...selectStyles.valueContainer(styles, state),
                              maxHeight: isFocusedType ? "auto" : "30px",
                              overflow: isFocusedType ? "visible" : "scroll",
                            }),
                          }}
                        />
                      </div>

                      <Tooltip disableInteractive title={"Date Range"} placement="top" arrow>
                        <div className="correspondenceDatePicker">
                          <Stack direction="column" spacing={8} alignItems="flex-start">
                            <DateRangePicker
                              ranges={predefinedBottomRanges}
                              cleanable={true}
                              placement="auto"
                              appearance="default"
                              format={"MM/dd/yyyy"}
                              className="form-date-control form-control-solid b-r-1 text-dark"
                              placeholder={"MM/DD/YYYY  - MM/DD/YYYY "}
                              name="date"
                              onChange={(e) => { handleOnDateTimeChange({ name: 'date', value: e }) }}
                              value={searchFormik?.values?.date || null}
                            />
                          </Stack>
                        </div>
                      </Tooltip>

                      <div>
                        <Tooltip disableInteractive title={"Search"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="submit" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              {loading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <BiSearch />
                              )}
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Reset"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="reset" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              <BiReset />
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Download"} placement="top" arrow>
                          <Button
                            className="btn btn-light btn-active-primary"
                            type="button"
                            onClick={handleExport}
                            disabled={exportLoading || loading}
                          >
                            {exportLoading ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <span className="svg-icon svg-icon-3">
                                <BsCloudDownloadFill />
                              </span>
                            )}
                          </Button>
                        </Tooltip>
                      </div>

                    </div>
                  </div>
                </Form>

                <div className="card-body p-common">
                  <div className="table-responsive">
                    <div className="dashboardtable">
                      <DataTable
                        className="custom-datatable"
                        paginationDefaultPage={pageno}
                        fixedHeader={true}
                        progressPending={loading}
                        data={Array.isArray(data?.results) ? data?.results : (Array.isArray(data) ? data : [])}
                        columns={columns}
                        pointerOnHover={true}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={limit}
                        paginationRowsPerPageOptions={[25, 50, 100, 150, 200]}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        conditionalRowStyles={conditionalRowStyles}
                        theme="solarized"
                        customStyles={datatableCustomStyles}
                        striped={true}
                        noDataComponent={isInitSearch ? <p className="p-4">Apply filters to refine and display the data</p> : <p className="p-4">There are no records to display</p>}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
}

export default SftpHouseKeepingLogs;
export { SftpHouseKeepingLogs as RetrieveSftpHouseKeeping };
